import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';

import { GENERIC_LAST_FIELD_CHANGED_ERROR, MSRP, RETAIL_PRICE } from '../../../constants';
import { dealLockHooks } from '../../../hooks';
import { usePaymentCall } from '../../../utils/usePaymentCall';
import { MAX_RETAIL_PRICE_THRESHOLD, NUMERIC_MAX_VALUE_MESSAGE } from '../constants';
import { useFieldValidationEffect } from './useFieldValidationEffect';

export const useRetailPriceField = () => {
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const dispatch = useDispatch();
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const {
        value: retailPrice,
        handleChange: handleRetailPriceChange,
        handleBlur,
        handleKeyDown
    } = usePaymentCall(
        offerSelectors.getRetailPriceOverrideWithFallback,
        offerReduxActionCreators.updateVehicleRetailPriceOverride,
        { formatValueToString: formatNumberAndCents }
    );

    const failed = useSelector(deskingSelectors.wasTheLastFailure(offerActionTypes.UPDATED_RETAIL_PRICE_OVERRIDE));
    const failedMessage = useMemo(() => (failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined), [failed]);
    const isVehicleNew = useSelector(vehicleSelectors.isVehicleNew);
    const isLocked = dealLockHooks.useIsDealLocked();
    const retailPriceValidationError = useSelector(
        dealScreenErrorSelectors.getFieldValidationError(DealTabs.SUMMARY, kebabCase(RETAIL_PRICE))
    );

    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        handleRetailPriceChange(e);
    };

    const handleOfferType = () => {
        if (offerType === CASH) dispatch(interactionAnalyticsActionCreators.cashMsrpTextInput());
        else if (offerType === FINANCE) dispatch(interactionAnalyticsActionCreators.financeMsrpTextInput());
        else if (offerType === LEASE) dispatch(interactionAnalyticsActionCreators.leaseMsrpTextInput());
    };

    const label = useMemo(() => {
        return isVehicleNew ? MSRP : RETAIL_PRICE;
    }, [isVehicleNew]);

    useFieldValidationEffect({
        isEnabled: isDealScreenRedDotErrorsEnabled,
        dispatch,
        failed,
        value: retailPrice,
        threshold: MAX_RETAIL_PRICE_THRESHOLD,
        validationError: retailPriceValidationError,
        fieldName: RETAIL_PRICE
    });

    return {
        label,
        inputProps: {
            name: kebabCase(RETAIL_PRICE),
            onChange: handleChange,
            placeholder: '26,500.00',
            value: retailPrice,
            hasError: failed,
            errorMessage: failedMessage,
            onBlur: (e: any) => {
                handleBlur(e);
                handleOfferType();
            },
            'data-testid': kebabCase(RETAIL_PRICE),
            allowDecimal: true,
            decimalMaxLength: 2,
            decimalMinLength: 0,
            disabled: isLocked,
            inputPrefix: '$',
            autoInsertCommas: true,
            maxValue: MAX_RETAIL_PRICE_THRESHOLD,
            customErrorMessages: {
                exceedsMaxValueRule: NUMERIC_MAX_VALUE_MESSAGE
            },
            size: 'sm' as const,
            onKeyDown: handleKeyDown
        }
    };
};
